// src/components/UserContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

interface UserContextType {
  user: User | null;
  customerData: any | null;
  loading: boolean;
  logout: () => Promise<void>;
  refreshCustomerData: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [customerData, setCustomerData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCustomerData = async (userId: string) => {
    try {
      const docRef = doc(db, 'customer_data', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setCustomerData(docSnap.data());
      } else {
        setCustomerData(null);
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      setCustomerData(null);
    }
  };

  const refreshCustomerData = async () => {
    if (user) {
      await fetchCustomerData(user.uid);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setCustomerData(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        await fetchCustomerData(user.uid);
      } else {
        setCustomerData(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = {
    user,
    customerData,
    loading,
    logout,
    refreshCustomerData
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};